import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { IngestionSourceParametersType, IngestionVolumeInfo } from '../../../gamma-api';
import IbTypography from '../common/IbTypography';
import IbTextCollapse from '../common/IbTextCollapse';
import SourceTitle from '../../pages/projects/ProjectPage/sources/SourceTitle';
import { isSourceType } from '../../utils/typeUtil';
import { ContentRenderMode } from '../../pages/projects/ProjectPage/sources/FragmentList';
import ContentSplitView from '../ContentSplitView';
import IbUrl from '../common/IbUrl';

import {
  CONTENT_CLASS_NAME,
  CONTENT_METADATA_CLASS_NAME,
  CONTENT_METADATA_KEY_CLASS_NAME,
  MAIN_CLASS_NAME,
  SCORE_CLASS_NAME,
  SYSTEM_METADATA_PREFIX,
  HEAD_CLASS_NAME,
  HEAD_TOP_ROW_CLASS_NAME,
  TITLE_CLASS_NAME,
  IGNORED_METADATA_KEYS,
  SOURCE_TYPE_METADATA_KEY,
} from './const';

interface ITextChunkProps {
  contentId?: string;
  chunk_num: number;
  score?: number;
  // eslint-disable-next-line @typescript-eslint/ban-types
  metadata: object;
  text: string;
  volume?: IngestionVolumeInfo;
  renderMode?: ContentRenderMode;
}

const TextChunk: React.FC<ITextChunkProps> = ({
  contentId,
  chunk_num,
  score,
  metadata,
  text,
  volume,
  renderMode = ContentRenderMode.MARKDOWN,
}) => {
  const { t, i18n } = useTranslation();

  const chunkWindowText = (metadata as { window?: string }).window;
  const url = (metadata as { url?: string }).url;

  const getDescription = () => {
    if (!volume) {
      return '';
    }

    return ` (${t('Token count', { count: volume.token_count || 0 })})`;
  };

  const getTitle = () => {
    const description = getDescription();

    return `${t('Text chunk: Index')} ${chunk_num}${description}:`;
  };

  const renderTitle = () => {
    return (
      <div className={TITLE_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {getTitle()}
        </IbTypography.Paragraph>
        {url && URL.canParse(url) && (
          <a href={url} rel="noreferrer" target="_blank">
            {url}
          </a>
        )}
      </div>
    );
  };

  const renderScore = () => {
    if (score == null) {
      return null;
    }

    return (
      <IbTypography.Paragraph className={SCORE_CLASS_NAME} type="secondary">
        {score.toFixed(3)}
      </IbTypography.Paragraph>
    );
  };

  const renderMetadata = (key: string, value: string) => {
    if (key.startsWith(SYSTEM_METADATA_PREFIX) || IGNORED_METADATA_KEYS.includes(key)) {
      return null;
    }

    const renderKey = (key: string) => {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      return i18n.exists(`Text chunk: Metadata key: {${key}}`) ? t(`Text chunk: Metadata key: {${key}}`) : key;
    };

    const renderValue = (key: string, value: string) => {
      if (key === SOURCE_TYPE_METADATA_KEY && isSourceType(value)) {
        return <SourceTitle name={t(`Source type: ${value}`)} type={value as IngestionSourceParametersType} />;
      }

      return <IbUrl url={value} />;
    };

    return (
      <div key={key} className={CONTENT_METADATA_CLASS_NAME}>
        <IbTypography.Paragraph disabled strong className={CONTENT_METADATA_KEY_CLASS_NAME} type="secondary">
          {renderKey(key)}:
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">{renderValue(key, value)}</IbTypography.Paragraph>
      </div>
    );
  };

  const renderMarkdownText = (text: string) => {
    return <ContentSplitView renderMode={renderMode} text={text} />;
  };

  const renderMainText = () => (
    <IbTextCollapse
      content={renderMarkdownText(text)}
      contentId={contentId}
      style={chunkWindowText ? 'accent' : 'default'}
    />
  );

  const renderWindowText = () =>
    chunkWindowText && <IbTextCollapse content={renderMarkdownText(chunkWindowText)} contentId={contentId} />;

  const renderHead = () => {
    return (
      <div className={HEAD_CLASS_NAME}>
        <div className={HEAD_TOP_ROW_CLASS_NAME}>
          {renderTitle()}
          {renderScore()}
        </div>
        {Object.entries(metadata).map(([key, value]) => renderMetadata(key, `${value || ''}`))}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        {renderMainText()}
        {renderWindowText()}
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderHead()}
      {renderContent()}
    </div>
  );
};

export default memo(TextChunk);
